import React, { useMemo } from 'react';

/**
 * IsometricStore — solid isometric grocery store scene.
 * Four zones mapped 1:1 to the "How it works" tabs:
 *   orders     → AISLES (parallel shelves + product blocks)
 *   suppliers  → LOADING DOCK (truck + stacked pallets)
 *   exceptions → CHECKOUT (POS counters + conveyor belts)
 *   contracts  → COLD CHAIN (walk-in fridges + temperature)
 *
 * Style: solid isometric blocks with subtle shading, thin outlines,
 * cream/blue palette matching the meuze.ai landing aesthetic.
 * Every zone has a passive ambient animation so the scene always
 * feels alive; the active zone gets a blue pulse + brighter accents.
 */

/* ---------- isometric projection helpers ---------- */
const K = 0.866; // cos 30
const S = 0.5;   // sin 30
const OX = 450;  // origin X
const OY = 90;   // origin Y
const U  = 22;   // grid unit (px) in world space

const iso = (x, y, z = 0) => ({
  x: OX + (x - y) * K * U,
  y: OY + (x + y) * S * U - z * U,
});

/* ---------- iso primitives ---------- */
const IsoBox = ({ x, y, w, d, h, top = '#e9e5d4', left = '#c9c2a8', right = '#a8a084', stroke = '#0b0b0e', strokeWidth = 1, opacity = 1 }) => {
  const a = iso(x,       y,       h); // top-back
  const b = iso(x + w,   y,       h); // top-right
  const c = iso(x + w,   y + d,   h); // top-front
  const d1 = iso(x,      y + d,   h); // top-left
  const e = iso(x + w,   y,       0); // bot-right
  const f = iso(x + w,   y + d,   0); // bot-front-r
  const g = iso(x,       y + d,   0); // bot-front-l
  return (
    <g opacity={opacity}>
      {/* right face */}
      <path d={`M${b.x},${b.y} L${c.x},${c.y} L${f.x},${f.y} L${e.x},${e.y} Z`} fill={right} stroke={stroke} strokeWidth={strokeWidth} strokeLinejoin="round" />
      {/* left face */}
      <path d={`M${c.x},${c.y} L${d1.x},${d1.y} L${g.x},${g.y} L${f.x},${f.y} Z`} fill={left} stroke={stroke} strokeWidth={strokeWidth} strokeLinejoin="round" />
      {/* top face */}
      <path d={`M${a.x},${a.y} L${b.x},${b.y} L${c.x},${c.y} L${d1.x},${d1.y} Z`} fill={top} stroke={stroke} strokeWidth={strokeWidth} strokeLinejoin="round" />
    </g>
  );
};

const IsoTile = ({ x, y, w, d, fill = '#efece0', stroke = '#0b0b0e', strokeWidth = 0.8, opacity = 1 }) => {
  const a = iso(x,     y);
  const b = iso(x + w, y);
  const c = iso(x + w, y + d);
  const dd = iso(x,    y + d);
  return (
    <path d={`M${a.x},${a.y} L${b.x},${b.y} L${c.x},${c.y} L${dd.x},${dd.y} Z`}
          fill={fill} stroke={stroke} strokeWidth={strokeWidth} strokeLinejoin="round" opacity={opacity} />
  );
};

/* ---------- floor + guide grid ---------- */
const FloorAndGrid = () => {
  const gw = 14, gd = 10;
  const a = iso(0, 0);
  const b = iso(gw, 0);
  const c = iso(gw, gd);
  const d = iso(0, gd);

  const gridLines = [];
  for (let i = 1; i < gw; i++) {
    const p1 = iso(i, 0), p2 = iso(i, gd);
    gridLines.push(<line key={`v${i}`} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#0b0b0e" strokeOpacity="0.06" strokeWidth="0.6" />);
  }
  for (let j = 1; j < gd; j++) {
    const p1 = iso(0, j), p2 = iso(gw, j);
    gridLines.push(<line key={`h${j}`} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#0b0b0e" strokeOpacity="0.06" strokeWidth="0.6" />);
  }
  return (
    <g>
      <path d={`M${a.x},${a.y} L${b.x},${b.y} L${c.x},${c.y} L${d.x},${d.y} Z`}
            fill="#f2efe1" stroke="#0b0b0e" strokeWidth="1.1" strokeLinejoin="round" />
      {gridLines}
    </g>
  );
};

/* =====================================================
   ZONE 1 · LOADING DOCK (suppliers)
===================================================== */
const LoadingDock = ({ active }) => {
  const dockFill = active ? '#dfe8ff' : '#efece0';
  return (
    <g>
      {/* dock floor */}
      <IsoTile x={0} y={0} w={5} d={4} fill={dockFill} />

      {/* dock edge (raised platform lip) */}
      <IsoBox x={0} y={0} w={5} d={0.15} h={0.35} top="#d9d1b3" left="#b3ac8f" right="#8f8973" />

      {/* pallets — three stacks */}
      <IsoBox x={0.6} y={0.6} w={0.9} d={0.9} h={0.7} top="#8b6f4a" left="#6f593a" right="#553f28" />
      <IsoBox x={0.6} y={0.6} w={0.9} d={0.9} h={1.4} top="#c9a26b" left="#a58254" right="#83653f" opacity={0.001} />
      <IsoBox x={1.9} y={0.9} w={1.0} d={1.0} h={0.5} top="#a37448" left="#805a37" right="#5f4127" />
      <IsoBox x={3.2} y={0.6} w={0.9} d={0.9} h={0.85} top="#8b6f4a" left="#6f593a" right="#553f28" />

      {/* boxes on pallets (2 stacks of cartons) */}
      <IsoBox x={0.65} y={0.65} w={0.8} d={0.8} h={0.75} top={active ? '#2E6BFF' : '#c7bfa4'} left={active ? '#2555d1' : '#a49d84'} right={active ? '#1c3f9b' : '#7f7a63'} />
      <IsoBox x={3.25} y={0.65} w={0.8} d={0.8} h={0.6} top="#d7cfb1" left="#b1a98d" right="#8b8571" />

      {/* Truck — cab + trailer, sits half-out */}
      <g className="iso-truck">
        {/* trailer */}
        <IsoBox x={-2.4} y={2.6} w={2.4} d={1.2} h={1.3} top="#e5e0ce" left="#c3bea8" right="#a09b86" />
        {/* cab */}
        <IsoBox x={-3.5} y={2.7} w={1.1} d={1.0} h={1.0} top="#2E6BFF" left="#2555d1" right="#1c3f9b" />
        {/* windshield */}
        <g>
          {(() => {
            const p1 = iso(-3.45, 2.75, 1.0);
            const p2 = iso(-2.45, 2.75, 1.0);
            const p3 = iso(-2.45, 2.75, 0.55);
            const p4 = iso(-3.45, 2.75, 0.55);
            return <path d={`M${p1.x},${p1.y} L${p2.x},${p2.y} L${p3.x},${p3.y} L${p4.x},${p4.y} Z`} fill="#0b0b0e" opacity="0.75" />;
          })()}
        </g>
        {/* wheels */}
        {[[-3.15, 3.72], [-2.35, 3.72], [-1.5, 3.72], [-0.7, 3.72]].map(([x, y], i) => {
          const p = iso(x, y, 0.15);
          return <circle key={i} cx={p.x} cy={p.y} r="4.5" fill="#0b0b0e" />;
        })}
      </g>

      {/* small crate icons moving up the dock */}
      <g className="iso-crate">
        <IsoBox x={4.2} y={2.3} w={0.5} d={0.5} h={0.35} top="#2E6BFF" left="#2555d1" right="#1c3f9b" />
      </g>

      {/* label */}
      <ZoneLabel x={0.3} y={3.5} text="DOCK · RECEIVING" active={active} />
    </g>
  );
};

/* =====================================================
   ZONE 2 · COLD CHAIN (contracts)
===================================================== */
const ColdChain = ({ active }) => {
  const floorFill = active ? '#dfe8ff' : '#efece0';
  return (
    <g>
      <IsoTile x={9} y={0} w={5} d={4} fill={floorFill} />

      {/* Row of walk-in fridges */}
      {[0, 1, 2].map((i) => {
        const bx = 9.4 + i * 1.5;
        const doorColor = active ? '#2E6BFF' : '#4a5a78';
        return (
          <g key={i}>
            {/* fridge body */}
            <IsoBox x={bx} y={0.5} w={1.2} d={1.2} h={2.2} top="#e5e0ce" left="#c1bba3" right="#948e78" />
            {/* door panel */}
            {(() => {
              const p1 = iso(bx + 1.2, 0.55, 2.15);
              const p2 = iso(bx + 1.2, 1.65, 2.15);
              const p3 = iso(bx + 1.2, 1.65, 0.15);
              const p4 = iso(bx + 1.2, 0.55, 0.15);
              return <path d={`M${p1.x},${p1.y} L${p2.x},${p2.y} L${p3.x},${p3.y} L${p4.x},${p4.y} Z`}
                           fill={doorColor} opacity={active ? 0.85 : 0.55} stroke="#0b0b0e" strokeWidth="0.8" />;
            })()}
            {/* handle */}
            {(() => {
              const p1 = iso(bx + 1.2, 0.7, 1.1);
              const p2 = iso(bx + 1.2, 0.75, 1.1);
              return <circle cx={(p1.x + p2.x) / 2} cy={p1.y} r="2" fill="#f2efe1" />;
            })()}
          </g>
        );
      })}

      {/* Temperature strip readout */}
      <g className={active ? 'iso-temp-pulse' : ''}>
        {(() => {
          const p = iso(9.4, 2.5);
          return (
            <>
              <rect x={p.x - 4} y={p.y + 8} width="120" height="20" rx="3" fill="#0b0b0e" opacity="0.85" />
              <text x={p.x + 4} y={p.y + 22} fontFamily="ui-monospace, monospace" fontSize="10" fill="#2E6BFF" letterSpacing="1.5">−18°C  ·  CHILL +2°C</text>
            </>
          );
        })()}
      </g>

      <ZoneLabel x={9.3} y={3.5} text="COLD CHAIN · CONTRACTS" active={active} />
    </g>
  );
};

/* =====================================================
   ZONE 3 · AISLES (orders)
===================================================== */
const Aisles = ({ active }) => {
  const floorFill = active ? '#dfe8ff' : '#efece0';
  return (
    <g>
      <IsoTile x={0} y={4} w={9} d={4} fill={floorFill} />

      {/* Four parallel shelving rows running along X */}
      {[0, 1, 2, 3].map((row) => {
        const yBase = 4.4 + row * 0.85;
        return (
          <g key={row}>
            {/* base shelf frame */}
            <IsoBox x={0.6} y={yBase} w={7.6} d={0.35} h={0.35}
                    top="#d9d1b3" left="#b3ac8f" right="#8f8973" />
            {/* products on shelf — colored blocks with a scan light pass */}
            {Array.from({ length: 10 }).map((_, i) => {
              const px = 0.7 + i * 0.75;
              const isBlue = active && ((i + row) % 3 === 1);
              const h = 0.5 + ((i * 7 + row * 3) % 5) * 0.06;
              return (
                <IsoBox
                  key={i}
                  x={px}
                  y={yBase + 0.02}
                  w={0.55}
                  d={0.28}
                  h={h}
                  top={isBlue ? '#2E6BFF' : ['#c8bfa2', '#b8ae90', '#d3caa9', '#a89f82'][(i + row) % 4]}
                  left={isBlue ? '#2555d1' : ['#a49b7f', '#948b70', '#aea587', '#847c62'][(i + row) % 4]}
                  right={isBlue ? '#1c3f9b' : ['#7f7761', '#726b53', '#89836a', '#645d48'][(i + row) % 4]}
                />
              );
            })}
          </g>
        );
      })}

      {/* Scan light sweeping across aisles when active */}
      {active && (
        <g className="iso-scan">
          {(() => {
            const p1 = iso(0.6, 4.3);
            const p2 = iso(0.6, 7.7);
            return (
              <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                    stroke="#2E6BFF" strokeWidth="2" opacity="0.65"
                    strokeDasharray="4 4" />
            );
          })()}
        </g>
      )}

      <ZoneLabel x={0.6} y={7.7} text="AISLES · ORDERS" active={active} />
    </g>
  );
};

/* =====================================================
   ZONE 4 · CHECKOUT (exceptions)
===================================================== */
const Checkout = ({ active }) => {
  const floorFill = active ? '#dfe8ff' : '#efece0';
  return (
    <g>
      <IsoTile x={9} y={4} w={5} d={4} fill={floorFill} />

      {/* three checkout counters (each: counter block + belt strip + POS terminal) */}
      {[0, 1, 2].map((i) => {
        const bx = 9.5 + i * 1.5;
        return (
          <g key={i}>
            {/* counter body */}
            <IsoBox x={bx} y={4.5} w={1.1} d={0.6} h={0.7}
                    top="#e5e0ce" left="#c1bba3" right="#948e78" />
            {/* conveyor belt strip */}
            <g className={active ? 'iso-belt' : ''}>
              {(() => {
                const p1 = iso(bx + 0.05, 4.55, 0.72);
                const p2 = iso(bx + 1.05, 4.55, 0.72);
                const p3 = iso(bx + 1.05, 5.05, 0.72);
                const p4 = iso(bx + 0.05, 5.05, 0.72);
                return (
                  <>
                    <path d={`M${p1.x},${p1.y} L${p2.x},${p2.y} L${p3.x},${p3.y} L${p4.x},${p4.y} Z`}
                          fill="#0b0b0e" opacity="0.85" />
                    <path d={`M${p1.x},${p1.y} L${p2.x},${p2.y} L${p3.x},${p3.y} L${p4.x},${p4.y} Z`}
                          fill="url(#beltStripes)" opacity="0.5" />
                  </>
                );
              })()}
            </g>
            {/* POS terminal */}
            <IsoBox x={bx + 0.05} y={5.2} w={0.4} d={0.4} h={0.55}
                    top={active && i === 1 ? '#2E6BFF' : '#3a4256'}
                    left={active && i === 1 ? '#2555d1' : '#2b3244'}
                    right={active && i === 1 ? '#1c3f9b' : '#1f2432'} />
            {/* exception flag on middle lane when active */}
            {active && i === 1 && (
              <g className="iso-flag">
                {(() => {
                  const p = iso(bx + 0.25, 5.4, 1.2);
                  return (
                    <>
                      <line x1={p.x} y1={p.y} x2={p.x} y2={p.y + 22}
                            stroke="#0b0b0e" strokeWidth="1.5" />
                      <path d={`M${p.x},${p.y} L${p.x + 14},${p.y + 4} L${p.x},${p.y + 8} Z`}
                            fill="#2E6BFF" stroke="#0b0b0e" strokeWidth="0.8" />
                    </>
                  );
                })()}
              </g>
            )}
          </g>
        );
      })}

      {/* Entrance doors on the right edge */}
      <IsoBox x={13.5} y={6.5} w={0.15} d={1.2} h={1.4} top="#2E6BFF" left="#2555d1" right="#1c3f9b" opacity={0.85} />

      <ZoneLabel x={9.4} y={7.7} text="CHECKOUT · EXCEPTIONS" active={active} />
    </g>
  );
};

/* ---------- zone label helper ---------- */
const ZoneLabel = ({ x, y, text, active }) => {
  const p = iso(x, y);
  return (
    <g>
      <rect x={p.x - 4} y={p.y - 2} width={Math.max(text.length * 6.2, 90)} height="14" rx="2"
            fill={active ? '#2E6BFF' : '#0b0b0e'} opacity={active ? 0.95 : 0.55} />
      <text x={p.x} y={p.y + 8}
            fontFamily="ui-monospace, monospace" fontSize="8"
            fill="#f2efe1" letterSpacing="1.5">{text}</text>
    </g>
  );
};

/* ---------- main component ---------- */
export default function IsometricStore({ activeKey }) {
  // Remap legacy keys (demand/labor/inventory/locations) to procurement zones
  const remap = { demand: 'orders', labor: 'exceptions', inventory: 'contracts', locations: 'suppliers' };
  const key = remap[activeKey] || activeKey || 'orders';

  const zones = useMemo(() => ({
    orders:     { orders: true,     suppliers: false,   exceptions: false, contracts: false },
    suppliers:  { orders: false,    suppliers: true,    exceptions: false, contracts: false },
    exceptions: { orders: false,    suppliers: false,   exceptions: true,  contracts: false },
    contracts:  { orders: false,    suppliers: false,   exceptions: false, contracts: true  },
  }), []);
  const a = zones[key] || zones.orders;

  return (
    <div className="relative w-full aspect-[16/11] overflow-hidden rounded-md"
         style={{ background: '#f5f4ee' }}
         data-testid="isometric-store">
      <svg viewBox="0 0 900 620" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
        <defs>
          {/* subtle dot texture for the floor */}
          <pattern id="floorDots" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.55" fill="#0b0b0e" opacity="0.14" />
          </pattern>
          {/* diagonal belt stripes */}
          <pattern id="beltStripes" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(30)">
            <rect width="3" height="6" fill="#2E6BFF" />
          </pattern>
        </defs>

        {/* soft outer shadow */}
        <ellipse cx="450" cy="560" rx="360" ry="18" fill="#0b0b0e" opacity="0.06" />

        <FloorAndGrid />
        {/* dot overlay on the floor for texture */}
        {(() => {
          const p1 = iso(0, 0), p2 = iso(14, 0), p3 = iso(14, 10), p4 = iso(0, 10);
          return (
            <path d={`M${p1.x},${p1.y} L${p2.x},${p2.y} L${p3.x},${p3.y} L${p4.x},${p4.y} Z`}
                  fill="url(#floorDots)" />
          );
        })()}

        {/* Active-zone soft glow layer */}
        <g style={{ transition: 'opacity 500ms ease' }}>
          <LoadingDock active={a.suppliers} />
          <ColdChain   active={a.contracts} />
          <Aisles      active={a.orders} />
          <Checkout    active={a.exceptions} />
        </g>
      </svg>

      {/* Corner labels */}
      <div className="absolute bottom-3 left-4 mono text-[9.5px] tracking-widest uppercase"
           style={{ color: 'rgba(11,11,14,0.55)' }}>
        grocera · store id 04 · live
      </div>
      <div className="absolute top-3 right-4 mono text-[10px] tracking-widest px-2.5 py-1 rounded"
           style={{ background: 'rgba(255,255,255,0.9)', color: '#2E6BFF', border: '1px solid rgba(46,107,255,0.35)' }}>
        ▸ {({
          orders: 'ORDERS · AISLES',
          suppliers: 'SUPPLIERS · DOCK',
          exceptions: 'EXCEPTIONS · CHECKOUT',
          contracts: 'CONTRACTS · COLD CHAIN',
        })[key]}
      </div>

      <style>{`
        .iso-truck   { animation: truckIn 6s ease-in-out infinite; transform-origin: center; }
        .iso-crate   { animation: crateMove 4s ease-in-out infinite; transform-origin: center; }
        .iso-scan    { animation: scanSweep 3s ease-in-out infinite; transform-origin: center; }
        .iso-belt path[fill*="beltStripes"] { animation: beltShift 1.6s linear infinite; }
        .iso-temp-pulse { animation: tempPulse 2.2s ease-in-out infinite; transform-origin: center; }
        .iso-flag    { animation: flagWave 1.6s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }

        @keyframes truckIn {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(6px, -3px); }
        }
        @keyframes crateMove {
          0%       { transform: translate(0, 0); opacity: 0; }
          20%      { opacity: 1; }
          80%      { opacity: 1; }
          100%     { transform: translate(-40px, 20px); opacity: 0; }
        }
        @keyframes scanSweep {
          0%, 100% { opacity: 0.2; transform: translate(0, 0); }
          50%      { opacity: 0.85; transform: translate(80px, -40px); }
        }
        @keyframes beltShift {
          0%   { transform: translate(0, 0); }
          100% { transform: translate(-6px, 3px); }
        }
        @keyframes tempPulse {
          0%, 100% { opacity: 0.75; }
          50%      { opacity: 1; }
        }
        @keyframes flagWave {
          0%, 100% { transform: rotate(0deg); }
          50%      { transform: rotate(-6deg); }
        }
      `}</style>
    </div>
  );
}
